import axios, {AxiosError, InternalAxiosRequestConfig} from 'axios';
import {BaseResponse} from "../models/baseResponse.ts";
import {ApiError} from "../models/apiError.ts";
import {firebaseAuthApi} from "./firebaseAuthApi.ts";
import NoDataResponse from "../models/noDataResponse.ts";

const apiUrl = import.meta.env.VITE_API_URL;

/** 로컬 개발 시 R2 커스텀 도메인이 localhost referer를 차단하므로 public R2 URL로 치환 */
const CDN_HOST = 'https://cdn.aivoca.app';
const DEV_R2_HOST = 'https://pub-774ae58410af4ccf8c767bd9ddfb4768.r2.dev';
const isDev = import.meta.env.DEV;

function replaceImageUrls(obj: unknown): unknown {
    if (!isDev || obj == null) return obj;
    if (typeof obj === 'string') {
        return obj.includes(CDN_HOST) ? obj.replace(CDN_HOST, DEV_R2_HOST) : obj;
    }
    if (Array.isArray(obj)) return obj.map(replaceImageUrls);
    if (typeof obj === 'object') {
        const result: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
            result[k] = replaceImageUrls(v);
        }
        return result;
    }
    return obj;
}

/**
 * 요청 본문에 dev 치환된 R2 호스트가 남아 있으면 cdn.aivoca.app 로 되돌린다.
 * 응답 인터셉터(replaceImageUrls)가 화면 표시용으로 치환한 URL이 폼/모달을 거쳐
 * 그대로 PATCH 페이로드로 다시 보내질 때 DB에 dev 호스트가 박히는 사고 방지.
 */
function reverseImageUrls(obj: unknown): unknown {
    if (!isDev || obj == null) return obj;
    if (typeof obj === 'string') {
        return obj.includes(DEV_R2_HOST) ? obj.replace(DEV_R2_HOST, CDN_HOST) : obj;
    }
    if (Array.isArray(obj)) return obj.map(reverseImageUrls);
    if (typeof obj === 'object') {
        // FormData / File / Blob 등은 그대로 둔다.
        if (
            obj instanceof FormData ||
            (typeof Blob !== 'undefined' && obj instanceof Blob) ||
            (typeof ArrayBuffer !== 'undefined' && obj instanceof ArrayBuffer) ||
            (typeof File !== 'undefined' && obj instanceof File)
        ) {
            return obj;
        }
        const result: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
            result[k] = reverseImageUrls(v);
        }
        return result;
    }
    return obj;
}

// Axios 인스턴스 생성
export const axiosInstance = axios.create({
    baseURL: apiUrl,
    timeout: 180000, // 3분 (AI 생성 API 등 오래 걸리는 요청 대응)
    headers: {
        'Content-Type': 'application/json',
    },
});

// 요청 인터셉터: 토큰 자동 추가 + dev R2 호스트 역치환
axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        const token = await firebaseAuthApi.getCurrentUserToken();
        if (token && config.headers) {
            config.headers['X-FIREBASE-TOKEN'] = token;
        }
        if (isDev && config.data != null && !(config.data instanceof FormData)) {
            config.data = reverseImageUrls(config.data);
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// 응답 인터셉터: BaseResponse 처리 + 로컬 개발 시 CDN URL 치환
axiosInstance.interceptors.response.use(
    (response) => {
        const data = response.data as BaseResponse<unknown>;

        if (data.errorCode !== 0) {
            throw new ApiError(data.errorCode, data.title, data.message);
        }

        // 로컬 개발 시 이미지 URL을 public R2 URL로 치환
        if (isDev && data.result) {
            data.result = replaceImageUrls(data.result);
            response.data = data;
        }

        return response;
    },
    (error: AxiosError) => {
        if (error.response) {
            const data = error.response.data as BaseResponse<unknown>;

            // 401 Unauthorized - 로그인 페이지로 리다이렉트
            if (error.response.status === 401) {
                localStorage.removeItem('user');
                const baseUrl = import.meta.env.BASE_URL || '/';
                window.location.href = `${baseUrl}login`;
            }

            const apiError = new ApiError(
                data?.errorCode || error.response.status,
                data?.title,
                data?.message || error.message
            );
            apiError.status = error.response.status;
            throw apiError;
        }

        if (error.request) {
            throw new ApiError(0, 'Network Error', '서버에 연결할 수 없습니다.');
        }

        throw new ApiError(0, 'Unknown Error', error.message);
    }
);

// 타입 안전한 API 호출 헬퍼
export const apiClient = {
    get: async <T>(url: string, params?: Record<string, unknown>) => {
        const response = await axiosInstance.get<BaseResponse<T>>(url, {params});
        return response.data.result as T;
    },

    postMultipart: async <T>(url: string, data?: unknown) => {
        const response = await axiosInstance.post<BaseResponse<T>>(url, data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data.result as T;
    },

    post: async <T>(url: string, data?: unknown) => {
        const response = await axiosInstance.post<BaseResponse<T>>(url, data);
        return response.data.result as T;
    },

    postNoData: async (url: string, data?: unknown) => {
        const response = await axiosInstance.post<NoDataResponse>(url, data);
        return response.data;
    },

    put: async <T>(url: string, data?: unknown) => {
        const response = await axiosInstance.put<BaseResponse<T>>(url, data);
        return response.data.result as T;
    },

    putNoData: async (url: string, data?: unknown) => {
        const response = await axiosInstance.put<NoDataResponse>(url, data);
        return response.data;
    },

    patch: async <T>(url: string, data?: unknown) => {
        const response = await axiosInstance.patch<BaseResponse<T>>(url, data);
        return response.data.result as T;
    },

    patchNoData: async (url: string, data?: unknown) => {
        const response = await axiosInstance.patch<NoDataResponse>(url, data);
        return response.data;
    },

    delete: async <T>(url: string) => {
        const response = await axiosInstance.delete<BaseResponse<T>>(url);
        return response.data.result as T;
    },

    deleteNoData: async <D = unknown>(url: string, data?: D) => {
        const response = await axiosInstance.delete<NoDataResponse>(url, {data});
        return response.data;
    }
};
